import React, { useCallback, useEffect, useState } from 'react';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import apiService from '../../../services/api';
import { SummaryQuestion, SummaryResponse, SurveyResponseRow } from '../types';

/**
 * Per-question results.
 *
 * Two halves, deliberately: the bars answer "what did people pick", and the
 * verbatim list answers "what did they actually say". The free text is where the
 * insight in a discovery survey lives, so it is shown in full rather than reduced
 * to a count of "answered".
 */

const TEAL = '#1A5F7A';
const GOLD = '#D4A843';

/** Free-text fields worth reading verbatim: Q7 plus every "other" box. */
const FREE_TEXT_SUFFIX = '_other';
const Q7 = 'q7_improvement';

const QuestionBar: React.FC<{
  label: string;
  count: number;
  denominator: number;
  highlight?: boolean;
}> = ({ label, count, denominator, highlight }) => {
  const pct = denominator > 0 ? Math.round((count / denominator) * 100) : 0;
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.4 }}>
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '13px',
            color: count > 0 ? '#111827' : '#9CA3AF',
            fontWeight: highlight ? 700 : 500,
            flexGrow: 1,
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', fontWeight: 700, color: '#111827', minWidth: 24, textAlign: 'right' }}>
          {count}
        </Typography>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6B7280', minWidth: 34, textAlign: 'right' }}>
          {pct}%
        </Typography>
      </Box>
      {/* A plain div bar rather than a chart library: one dependency-free element
          per option reads better than 9 tiny BarCharts and cannot collapse to
          zero height. */}
      <Box sx={{ height: 8, borderRadius: 999, bgcolor: '#F3F4F6', overflow: 'hidden' }}>
        <Box
          sx={{
            width: `${pct}%`,
            height: '100%',
            bgcolor: highlight ? GOLD : TEAL,
            transition: 'width .3s ease',
          }}
        />
      </Box>
    </Box>
  );
};

interface Props {
  refreshKey?: number;
  /** Which survey to show. Null while the switcher is still loading. */
  campaign?: string | null;
}

export const QuestionBreakdownTab: React.FC<Props> = ({ refreshKey = 0, campaign = null }) => {
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [rows, setRows] = useState<SurveyResponseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The aggregation already exists server-side; the rows are only fetched for
      // the verbatim free text, not to recompute the counts client-side.
      const [s, r] = await Promise.all([
        apiService.getSurveySummary(campaign || undefined),
        apiService.getSurveyResponses({ limit: 500, campaign: campaign || undefined }),
      ]);
      setSummary(s || null);
      setRows(r?.responses || []);
    } catch (e: any) {
      setError(e?.message || 'Could not load the question breakdown');
    }
    setLoading(false);
    // `campaign` MUST be here. Without it the memoised loader captures whatever the
    // prop was on first render — null, before the switcher has resolved — so every
    // later fetch went out unscoped and the Lambda served the default campaign. The
    // effect below re-fired on a switch and called this same stale function.
  }, [campaign]);

  useEffect(() => {
    load();
  }, [load, refreshKey, campaign]);

  if (loading) {
    return (
      <Box>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={180} sx={{ borderRadius: 2, mb: 2 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: '#7D1935' }}>
        {error}
      </Typography>
    );
  }

  if (!summary || summary.total_responses === 0) {
    return (
      <Paper sx={{ p: 4, borderRadius: '14px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', color: '#6B7280' }}>
          No responses yet. Answers will appear here as members complete the survey.
        </Typography>
      </Paper>
    );
  }

  const total = summary.total_responses;

  /** Every non-empty free-text answer for a question, with who said it. */
  const verbatims = (q: SummaryQuestion): Array<{ email: string; text: string }> => {
    const field = q.key === Q7 ? Q7 : `${q.key}${FREE_TEXT_SUFFIX}`;
    return rows
      .map((r) => ({ email: String(r.email || ''), text: String(r[field] || '').trim() }))
      .filter((v) => v.text !== '');
  };

  return (
    <Box>
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#6B7280', mb: 2 }}>
        Percentages are of the {total} member{total === 1 ? '' : 's'} who completed the survey.
        Questions marked “choose all that apply” can total more than 100%.
      </Typography>

      {summary.questions.map((q) => {
        const said = verbatims(q);
        const isMulti = q.type === 'multi';
        // Multi-select is out of the people who answered it; single-select out of
        // everyone, so an unanswered optional question reads as low rather than
        // being quietly rebased.
        const denominator = isMulti ? q.answered || total : total;
        const top = q.options ? Math.max(...q.options.map((o) => o.count)) : 0;

        return (
          <Paper key={q.key} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
              <Typography sx={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17px', fontWeight: 700, color: '#111827', flexGrow: 1 }}>
                {q.prompt}
              </Typography>
              <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6B7280', whiteSpace: 'nowrap' }}>
                {isMulti ? 'choose all that apply · ' : ''}
                {q.answered} answered
              </Typography>
            </Box>

            {q.options?.map((o) => (
              <QuestionBar
                key={o.id}
                label={o.label}
                count={o.count}
                denominator={denominator}
                highlight={o.count > 0 && o.count === top}
              />
            ))}

            {said.length > 0 && (
              <Box sx={{ mt: q.options ? 2 : 0, pt: q.options ? 2 : 0, borderTop: q.options ? '1px solid #F3F4F6' : 'none' }}>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#6B7280', mb: 1 }}>
                  In their words ({said.length})
                </Typography>
                {said.map((v, i) => (
                  <Box key={`${v.email}-${i}`} sx={{ mb: 1.2, pl: 1.5, borderLeft: '3px solid #E5E7EB' }}>
                    <Typography sx={{ fontFamily: "'Lora', Georgia, serif", fontSize: '14px', lineHeight: 1.6, color: '#1F2937', whiteSpace: 'pre-wrap' }}>
                      “{v.text}”
                    </Typography>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#9CA3AF' }}>
                      {v.email}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

export default QuestionBreakdownTab;
